import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import { truncateKeywordSpaces } from 'utils/utils';
import { Autocomplete } from 'components/FormFields';
import { useDispatch, useSelector } from 'react-redux';
import { vehicleActions } from 'state/modules/vehicles';

const VehiclesTypesSelect = ({
  disabled = false,
  customParams = {},
  label = 'Select...',
  variant = 'standard',
  field: { name, value },
  form: { errors, touched, setFieldValue },
}) => {
  const dispatch = useDispatch();
  const isMounted = useRef(false);
  const [defaultValue, setDefaultValue] = useState(null);
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
    ...customParams,
  });

  const vehiclesTypes = useSelector((state) => state.vehicles?.vehiclesTypes);
  const loadingVehiclesTypes = useSelector((state) => state.vehicles?.loadingVehiclesTypes);

  const loading = loadingVehiclesTypes;

  const fetchVehiclesTypes = useCallback((params) => {
    const newParams = { ...params };
    if (!newParams.asset_class) {
      delete newParams.asset_class;
      dispatch(vehicleActions.fetchVehiclesTypes(newParams));
    }
    // Override id over keyword
    if (newParams?.keyword && newParams?.id) {
      delete newParams.id;
    }
    // Truncate keyword
    if (newParams?.keyword) {
      newParams.keyword = truncateKeywordSpaces(newParams.keyword);
    }
    dispatch(vehicleActions.fetchVehiclesTypes(newParams));
  }, []); // eslint-disable-line

  const fetchVehiclesTypesDebounced = useCallback(
    () =>
      debounce((params) => {
        const paramsNoId = { ...params };
        delete paramsNoId.id;
        fetchVehiclesTypes(paramsNoId);
      }, 1000),
    [fetchVehiclesTypes]
  );

  useEffect(() => {
    if (value) {
      fetchVehiclesTypes(params);
    }
  }, [fetchVehiclesTypes, value, params]);

  useEffect(() => {
    if (isMounted.current) {
      fetchVehiclesTypesDebounced(params);
    } else {
      isMounted.current = true;
    }
  }, [fetchVehiclesTypes, params]); // eslint-disable-line

  useEffect(() => {
    if ((value || value === '' || !defaultValue) && !params.keyword) {
      const defaultVal = vehiclesTypes.find((el) => el.id === parseInt(value));
      if (defaultVal) {
        setDefaultValue(defaultVal);
      } else {
        setDefaultValue(null);
      }
    }
  }, [value, vehiclesTypes, params]); // eslint-disable-line

  const handleFetchVehiclesTypes = () => {
    if (vehiclesTypes.length === 0 && !loading && !params?.keyword) fetchVehiclesTypes();
  };

  return (
    <Autocomplete
      label={label}
      margin="normal"
      fullwidth="true"
      variant={variant}
      disabled={disabled}
      value={defaultValue}
      options={vehiclesTypes}
      id="vehiclesTypesSelect"
      loading={loading}
      onHighlightChange={handleFetchVehiclesTypes}
      onChange={(ev, val) => setFieldValue(name, val?.id || '')}
      error={errors && errors[name] && touched && touched[name]}
      getOptionSelected={(option, value) => option?.id === value?.id}
      onInputChange={(ev, val) =>
        setParams((prevParams) => setParams({ ...prevParams, keyword: val }))
      }
      helperText={errors && errors[name] && touched && touched[name] && errors[name]}
      getOptionLabel={(option) =>
        `${option?.year ? option?.year + ' ' : ''}${option.make ? option.make + ' ' : ''}${
          option.model || ''
        }`
      }
    />
  );
};

VehiclesTypesSelect.propTypes = {
  label: PropTypes.string,
  disabled: PropTypes.bool,
  field: PropTypes.shape({
    value: PropTypes.any,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
  }),
  form: PropTypes.shape({
    errors: PropTypes.object,
    touched: PropTypes.object,
    setFieldValue: PropTypes.func,
  }),
  customParams: PropTypes.object,
  variant: PropTypes.oneOf(['standard', 'outlined', 'filled']),
};

export default VehiclesTypesSelect;
